import React, { useEffect, useRef, useState } from "react";
import Loading from "../../componants/Loading";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Auth from "../../componants/onBoarding/Auth";
import ConfirmEmail from "../../componants/onBoarding/ConfirmEmail";
import Details from "../../componants/onBoarding/Details";
import ProfessionalInfo from "../../componants/onBoarding/ProfessionalInfo";
import About from "../../componants/onBoarding/About";
import Socials from "../../componants/onBoarding/Socials";
import ProductServices from "../../componants/onBoarding/ProductServices";
import Links from "../../componants/onBoarding/Links";
import Images from "../../componants/onBoarding/Images";
import Videos from "../../componants/onBoarding/Videos";
import Calendly from "../../componants/onBoarding/Calendly";
import ProfilePicture from "../../componants/onBoarding/ProfilePicture";

import "./Onboarding.css";

import { getCollaborator } from "../../redux/appSlices/collaborator/collaboratorSlice";

import { onboardingTranslation, translation } from "../../translation";
import { useGetCollaboratorQuery } from "../../redux/appSlices/collaborator/collaboratorApiSlice";
import { usePatchCollaboratorMutation } from "../../redux/appSlices/onboarding/onboardingApiSlice";
import { apiSlice } from "../../redux/api/apiSlice";
import { toast } from "react-toastify";
import useLoggedStatus from "../../hooks/useLoggedStatus";
import { profileRedirectMap } from "../../constants";

const Onboarding = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const logged = useLoggedStatus();

  const { data, isLoading } = useGetCollaboratorQuery(id);
  const [patchCollaborator] = usePatchCollaboratorMutation();

  const [onboardingStep, setOnboardingStep] = useState(null);
  const [finalStep, setFinalStep] = useState(false);
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState(
    <Loading />,
  );
  const completedRedirectRef = useRef(false);

  const lang =
    useSelector((state) => state.collaborator.collaboratorInfo.language) ??
    "fr";

  useEffect(() => {
    if (!id) return;
    const redirectedId = profileRedirectMap[`/${id}`]?.replace("/", "");
    if (redirectedId) {
      navigate(`/profile/${redirectedId}`, { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    if (data) {
      const rawStep = data.data.collaborator.onboarding_step;
      const stepNum =
        rawStep === null || rawStep === undefined ? null : Number(rawStep);
      dispatch(getCollaborator(data.data.collaborator));
      setOnboardingStep(Number.isFinite(stepNum) ? stepNum : null);
      if (Number.isFinite(stepNum) && stepNum >= 11) {
        setFinalStep(true);
      }
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (completedRedirectRef.current) return;

    const stepNum = Number(
      onboardingStep ?? data?.data?.collaborator?.onboarding_step,
    );
    if (!Number.isFinite(stepNum) || stepNum < 11) return;

    const collabId = data?.data?.collaborator?.collaborator_id;
    if (!collabId) return;

    completedRedirectRef.current = true;
    const redirectedId =
      profileRedirectMap[`/${collabId}`]?.replace("/", "") ?? collabId;
    navigate(`/profile/${redirectedId}`, { replace: true });
  }, [data, onboardingStep, navigate]);

  useEffect(() => {
    if (
      onboardingStep !== null &&
      onboardingStep > 0 &&
      !logged &&
      !isLoading
    ) {
      navigate("/login", { replace: true });
    }
  }, [onboardingStep, logged, isLoading, navigate]);

  const applyStepToCacheAndStore = (step) => {
    setOnboardingStep(step);
    dispatch(
      apiSlice.util.updateQueryData("getCollaborator", id, (draft) => {
        if (draft?.data?.collaborator) {
          draft.data.collaborator.onboarding_step = step;
        }
      }),
    );
    if (data?.data?.collaborator) {
      dispatch(
        getCollaborator({
          ...data.data.collaborator,
          onboarding_step: step,
        }),
      );
    }
  };

  const handlePreviousStep = () => {
    const collab = data?.data?.collaborator;
    const patchId = collab?._id ?? id;
    const current = Number(onboardingStep);
    if (!patchId || !Number.isFinite(current)) return;
    const prev = current - 1;
    patchCollaborator({
      id: patchId,
      content: { onboarding_step: prev },
    })
      .unwrap()
      .then(() => applyStepToCacheAndStore(prev))
      .catch(() => toast.error(translation?.updateErrorMessage?.[lang] ?? ""));
  };

  const handleNextStep = () => {
    const collab = data?.data?.collaborator;
    const patchId = collab?._id ?? id;
    const current = Number(onboardingStep);
    if (!patchId || !Number.isFinite(current)) return;
    const next = current + 1;
    patchCollaborator({
      id: patchId,
      content: { onboarding_step: next },
    })
      .unwrap()
      .then(() => applyStepToCacheAndStore(next))
      .catch(() => toast.error(translation?.saveErrorMessage?.[lang] ?? ""));
  };

  const steps = [
    <Auth />,
    <ConfirmEmail />,
    <Details />,
    <ProfessionalInfo />,
    <About />,
    <Socials />,
    <ProductServices />,
    <Calendly />,
    <Links />,
    <Images />,
    <Videos />,
    <ProfilePicture setFinalStep={setFinalStep} />,
  ];

  useEffect(() => {
    if (onboardingStep == null || onboardingStep >= 11) return;

    if (onboardingStep >= -1) {
      const stepComponent = steps[onboardingStep + 1];
      setCurrentOnboardingStep(stepComponent ?? <Loading />);
    }
  }, [onboardingStep, finalStep]);

  if (!data && !isLoading) {
    window.location.href = "https://www.getpopcard.com/";
  }

  if (isLoading) return <Loading />;
  if (onboardingStep >= 11) return <Loading />;
  if (onboardingStep !== null && onboardingStep > 0 && !logged) {
    return <Loading />;
  }
  if (onboardingStep >= -1 && onboardingStep != null)
    return (
      <div
        className={
          lang === "ar" ? "font-readexRegular" : "font-SteradianRegular"
        }
      >
        {/* the header of onboarding sections */}
        {onboardingStep >= 0 && logged && (
          <div
            className={`flex pt-[35px] mx-[26px] ${
              onboardingStep !== 1 ? "justify-between" : "justify-end"
            }`}
            dir="ltr"
          >
            {onboardingStep !== 1 && (
              <div className="cursor-pointer" onClick={handlePreviousStep}>
                <img src="../../assets/img/icons/arrow-back.svg" alt="back" />
              </div>
            )}
            {onboardingStep >= 1 && onboardingStep < 10 && (
              <div
                className={`cursor-pointer ${lang === "ar" ? "font-lalezarRegular" : "font-SteradianMedium"}`}
                onClick={handleNextStep}
              >
                {onboardingTranslation?.buttonPass?.[lang]}
              </div>
            )}
          </div>
        )}
        {/* END the header of onboarding sections */}
        {currentOnboardingStep}
      </div>
    );
  return <Loading />;
};

export default Onboarding;
